import { Link, useParams } from "react-router-dom";
import { Check } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { Reveal, ClipImage } from "@/components/Motion";
import { CallButton, ConsultButton, TextLink } from "@/components/Buttons";
import { ServiceAreaMap } from "@/components/ServiceAreaMap";
import { CTABand } from "@/components/CTABand";
import { usePageMeta } from "@/components/Common";
import { COMMUNITIES } from "@/data/site";
import { NotFound } from "@/pages/Legal";
import { CityDetails } from "@/components/CityDetails";
import { EquestrianSection } from "@/components/EquestrianSection";
import { FaqSection } from "@/components/FaqSection";

const Body = ({ c }) => {
  usePageMeta(
    c.metaTitle || `Home Watch ${c.name}, Florida | First Due Home Watch & Concierge`,
    c.metaDesc || `Residential home watch in ${c.name}, FL: scheduled visits, photo reports and personalized quotes. Veteran-owned, firefighter-founded. Call 561-722-3224.`
  );
  const others = COMMUNITIES.filter((o) => o.slug !== c.slug);
  return (
    <main data-testid={`community-page-${c.slug}`}>
      <PageHero testId="community-hero" eyebrow={`Home Watch & Concierge · ${c.name}, Florida`} lines={[`Home Watch in`, `${c.name}, Florida.`]} intro={c.lead} img={c.img} alt={c.alt}>
        <ConsultButton testId="community-consult-btn" dark />
        <CallButton testId="community-call-btn" dark />
      </PageHero>
      <section className="bg-white py-20 md:py-28">
        <div className="mx-auto grid max-w-[1400px] gap-12 px-5 md:px-10 lg:grid-cols-12 lg:items-center">
          <ClipImage src={c.img} alt={c.alt} className="h-72 rounded-[4px] md:h-[460px] lg:col-span-5" />
          <Reveal className="lg:col-span-6 lg:col-start-7">
            <p className="eyebrow">Serving {c.name}</p>
            <h2 className="mt-4 font-display text-4xl font-semibold text-[#14263D] md:text-5xl">A local contact for your {c.name} home</h2>
            <p className="mt-6 text-base leading-relaxed text-[#5F6B76] md:text-lg">{c.body}</p>
            <ul className="mt-6 flex flex-wrap gap-2">
              {c.points.map((p) => (
                <li key={p} className="flex items-center gap-2 rounded-full bg-[#F8F7F3] px-4 py-2 text-sm text-[#14263D]"><Check className="h-3.5 w-3.5 text-[#A52A32]" />{p}</li>
              ))}
            </ul>
            <div className="mt-8 flex flex-wrap gap-6">
              <TextLink to="/home-watch-services" testId="community-services-link">Home Watch Services</TextLink>
              <TextLink to="/plans-and-pricing" testId="community-pricing-link">Home Watch Plans</TextLink>
            </div>
          </Reveal>
        </div>
      </section>
      {c.sections && <CityDetails c={c} />}
      {c.equestrian && <EquestrianSection e={c.equestrian} />}
      {c.faqs && <FaqSection items={c.faqs} eyebrow={`${c.name} Questions`} lines={["Home Watch in", `${c.name}: FAQ`]} className="bg-[#F8F7F3]" />}
      <section className={`${c.faqs ? "bg-white" : "bg-[#F8F7F3]"} py-20 md:py-28`}>
        <div className="mx-auto grid max-w-[1400px] gap-12 px-5 md:px-10 lg:grid-cols-12 lg:items-center">
          <Reveal className="lg:col-span-7"><ServiceAreaMap active={c.name} /></Reveal>
          <div className="lg:col-span-4 lg:col-start-9">
            <p className="eyebrow">Also serving</p>
            <ul className="mt-6">
              {others.map((o) => (
                <li key={o.slug}>
                  <Link to={`/service-areas/${o.slug}`} data-testid={`community-other-${o.slug}`} className="group flex items-baseline justify-between border-b border-[#14263D]/15 py-4">
                    <span className="font-display text-3xl text-[#14263D] transition-transform duration-300 group-hover:translate-x-2">{o.name}</span>
                    <span className="font-mono text-xs text-[#A52A32]">View</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>
      <CTABand testId="community-cta" />
    </main>
  );
};

export default function CommunityPage() {
  const { slug } = useParams();
  const c = COMMUNITIES.find((x) => x.slug === slug);
  return c ? <Body c={c} /> : <NotFound />;
}
